import * as THREE from 'three';
import { clamp, hash01, hash3, lerp, smoothstep } from '@/core/rng';

/**
 * The divine verb spectacle. When a verb lands, the moment itself deserves to
 * be seen — the crack of the strike, the gathering rain, the green shimmer,
 * the dun contraction. Render-only and fire-and-forget: main.ts calls spawn()
 * the instant a verb enters the world, and every persistent consequence (the
 * fire, the moisture, the biomass) belongs to the simulation, never to this
 * module. All variation derives from hash01 of the spawn position, all motion
 * from tSec and accumulated dt — no wall clock, no Math.random, so every
 * witness of the same verb at the same place sees the same spectacle.
 */
export interface VerbFx {
  group: THREE.Group;
  /** Fire-and-forget: called by main.ts the moment a verb lands. y = ground height. */
  /**
   * `radius` is the cast's real footprint in metres (see sim/simulation.ts
   * focusFor). Omit it and the spectacle falls back to the old grove-sized
   * 420 m, which is what every focus used to get regardless of the gesture.
   */
  spawn(verb: 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen',
        x: number, y: number, z: number, radius?: number): void;
  update(tSec: number, dt: number): void;
}

type Verb = 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen';

const MAX_FX = 4;              // concurrent effects; the oldest is evicted
/** Fallback footprint when a caller does not say how wide its cast was. */
const R = 420;
const TAU = Math.PI * 2;
const BOLT_TOP = 600;          // strike descends from this far up
const PT_MAX = 5;              // bolt polyline points: top + up to 3 kinks + impact

// per-effect particle budgets, and the shared instanced-mesh caps
const RAIN_N = 200, MOTE_N = 60, DUST_N = 40;
const RAIN_CAP = MAX_FX * RAIN_N;
const MOTE_CAP = MAX_FX * MOTE_N;
const DUST_CAP = MAX_FX * DUST_N;

// lifetimes in seconds — spectacle, then gone
const DUR: Record<Verb, number> =
  { strike: 0.65, rain: 22, strengthen: 8.5, weaken: 8.5, omen: 4.5 };

/**
 * HOW BIG THIS CAST IS, against the grove-sized footprint the spectacle was
 * tuned at. 1 at `R`, so every number below is unchanged at the scale it was
 * measured for and only close and watershed casts move.
 *
 * The footprint reached the *placement* of things — where a mote sits in the
 * disc, how wide the ring grows — but almost nothing else. Every particle's
 * own SIZE, every fall height, every rise speed and every particle COUNT was a
 * hard metre or a hard integer, so as the footprint shrank the spectacle did
 * not: a Strengthen channelled into one tree put sixty motes 2.6–5.8 m across
 * into a five-metre circle, a Weaken threw 16–30 m dust puffs across a
 * sapling, and a Rain on one plant still hung a 900 m storm quad 265 m
 * overhead. At Presence altitude those do not read as a verb at all; they read
 * as weather bolted onto a plant.
 *
 * Counts fall on the SQUARE ROOT, not linearly. Particles are scattered over a
 * disc, so holding the count while the radius shrinks multiplies density by
 * the area ratio — the failure is crowding, not quantity, and √ is the factor
 * that keeps apparent density constant. The floors matter as much as the
 * curve: a handful of particles still has to read as a deliberate act.
 */
const gauge = (radius: number): number => clamp(radius / R, 0.05, 2.5);
/** Particle count for a footprint, never below something you can see. */
const countFor = (n: number, g: number, floor = 8): number =>
  Math.max(floor, Math.round(n * clamp(Math.sqrt(g), 0.18, 1)));

/** Soft radial falloff — flash, mist, motes and (stretched) dust. */
function softDiscTexture(): THREE.CanvasTexture {
  const s = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, s, s);
  const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, 'rgba(255,255,255,0.85)');
  grad.addColorStop(0.55, 'rgba(255,255,255,0.38)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A soft annulus band at 0.8 of the half-width: plane scale = radius * 2.5. */
function ringTexture(): THREE.CanvasTexture {
  const s = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, s, s);
  const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.62, 'rgba(255,255,255,0)');
  grad.addColorStop(0.8, 'rgba(255,255,255,0.8)');
  grad.addColorStop(0.93, 'rgba(255,255,255,0.18)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A falling streak: bright vertical core, feathered ends. */
function streakTexture(): THREE.CanvasTexture {
  const w = 16, h = 64;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, w, h);
  const across = g.createLinearGradient(0, 0, w, 0);
  across.addColorStop(0, 'rgba(255,255,255,0)');
  across.addColorStop(0.5, 'rgba(255,255,255,0.9)');
  across.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = across;
  g.fillRect(0, 0, w, h);
  const along = g.createLinearGradient(0, 0, 0, h);
  along.addColorStop(0, 'rgba(255,255,255,0)');
  along.addColorStop(0.2, 'rgba(255,255,255,1)');
  along.addColorStop(0.8, 'rgba(255,255,255,1)');
  along.addColorStop(1, 'rgba(255,255,255,0)');
  g.globalCompositeOperation = 'destination-in';
  g.fillStyle = along;
  g.fillRect(0, 0, w, h);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** The clouds.ts blob, seeded and dense — a storm pancake, not fair weather. */
function stormTexture(): THREE.CanvasTexture {
  const s = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const g = cv.getContext('2d')!;
  const rnd = (() => { let a = 40093; return () => ((a = (a * 1664525 + 1013904223) >>> 0) / 4294967296); })();
  g.clearRect(0, 0, s, s);
  for (let i = 0; i < 9; i++) {
    const x = s * (0.3 + rnd() * 0.4), y = s * (0.34 + rnd() * 0.32);
    const r = s * (0.16 + rnd() * 0.22);
    const grad = g.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.6)');
    grad.addColorStop(0.6, 'rgba(255,255,255,0.3)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Two unit quads crossed at 90°, centred on the origin — reads from any azimuth. */
function crossedStreak(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    -0.5, -0.5, 0,   0.5, -0.5, 0,   0.5, 0.5, 0,   -0.5, 0.5, 0,
     0, -0.5, -0.5,  0, -0.5, 0.5,   0, 0.5, 0.5,    0, 0.5, -0.5,
  ]), 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
    0, 0, 1, 0, 1, 1, 0, 1,
    0, 0, 1, 0, 1, 1, 0, 1,
  ]), 2));
  geo.setIndex([0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7]);
  return geo;
}

interface Slot {
  verb: Verb | null;
  /** This cast's footprint in metres — the spectacle is sized to the act. */
  radius: number;
  x: number; y: number; z: number;
  age: number;
  seed: number;
  bolt: THREE.Mesh; boltGeo: THREE.BufferGeometry;
  boltPos: THREE.BufferAttribute; boltMat: THREE.MeshBasicMaterial;
  flash: THREE.Mesh; flashMat: THREE.MeshBasicMaterial;
  cloud: THREE.Mesh; cloudMat: THREE.MeshBasicMaterial;
  mist: THREE.Mesh; mistMat: THREE.MeshBasicMaterial;
  bloomRing: THREE.Mesh; bloomMat: THREE.MeshBasicMaterial;
  witherRing: THREE.Mesh; witherMat: THREE.MeshBasicMaterial;
}

// scratch for the bolt polyline — spawn-time only, but still preallocated
const ptX = new Float32Array(PT_MAX);
const ptY = new Float32Array(PT_MAX);
const ptZ = new Float32Array(PT_MAX);

/** Jagged descent baked into the slot's preallocated buffer. Same spot, same bolt. */
function buildBolt(s: Slot): void {
  const h = (i: number, salt: number) => hash01(s.seed, i, salt);
  const kinks = h(0, 11) < 0.5 ? 2 : 3;
  const n = kinks + 2;
  const topX = s.x + (h(1, 13) - 0.5) * 170;
  const topZ = s.z + (h(2, 13) - 0.5) * 170;
  ptX[0] = topX; ptY[0] = s.y + BOLT_TOP; ptZ[0] = topZ;
  for (let j = 1; j <= kinks; j++) {
    const t = j / (n - 1);
    const amp = 62 * (1 - 0.55 * t);          // wilder up high, converging on the mark
    ptX[j] = lerp(topX, s.x, t) + (h(j, 17) - 0.5) * 2 * amp;
    ptY[j] = s.y + BOLT_TOP * (1 - t);
    ptZ[j] = lerp(topZ, s.z, t) + (h(j, 23) - 0.5) * 2 * amp;
  }
  ptX[n - 1] = s.x; ptY[n - 1] = s.y; ptZ[n - 1] = s.z;   // the impact is exact

  const arr = s.boltPos.array as Float32Array;
  for (let p = 0; p < n; p++) {
    const w = lerp(6, 2.4, p / (n - 1));       // tapers toward the ground
    const px = ptX[p], py = ptY[p], pz = ptZ[p];
    const o = p * 12;
    arr[o] = px - w; arr[o + 1] = py; arr[o + 2] = pz;
    arr[o + 3] = px + w; arr[o + 4] = py; arr[o + 5] = pz;
    arr[o + 6] = px; arr[o + 7] = py; arr[o + 8] = pz - w;
    arr[o + 9] = px; arr[o + 10] = py; arr[o + 11] = pz + w;
  }
  s.boltPos.needsUpdate = true;
  s.boltGeo.setDrawRange(0, (n - 1) * 12);     // 12 indices per crossed segment
}

export function createVerbFx(): VerbFx {
  const group = new THREE.Group();

  const discTex = softDiscTexture();
  const ringTex = ringTexture();
  const streakTex = streakTexture();
  const stormTex = stormTexture();

  const flatGeo = new THREE.PlaneGeometry(1, 1);
  flatGeo.rotateX(-Math.PI / 2);
  const streakGeo = crossedStreak();

  // ---- shared instanced particles, filled fresh each frame from slot hashes
  const rain = new THREE.InstancedMesh(streakGeo, new THREE.MeshBasicMaterial({
    map: streakTex, transparent: true, opacity: 0.55, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: true,
    color: new THREE.Color(0.45, 0.55, 0.68),
  }), RAIN_CAP);
  const motes = new THREE.InstancedMesh(flatGeo, new THREE.MeshBasicMaterial({
    map: discTex, transparent: true, opacity: 0.8, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: true,
    color: new THREE.Color(0.72, 0.82, 0.3),
  }), MOTE_CAP);
  const dust = new THREE.InstancedMesh(flatGeo, new THREE.MeshBasicMaterial({
    map: discTex, transparent: true, opacity: 0.22, depthWrite: false,
    side: THREE.DoubleSide, fog: true,
    color: new THREE.Color(0.6, 0.53, 0.4),
  }), DUST_CAP);
  rain.name = 'verb-rain'; motes.name = 'verb-motes'; dust.name = 'verb-dust';
  for (const m of [rain, motes, dust]) {
    m.frustumCulled = false;
    m.renderOrder = 2;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.count = 0;
  }
  group.add(rain, motes, dust);

  // ---- one white point-light pulse, claimed by the newest strike
  const light = new THREE.PointLight(0xe8eeff, 0, 1400, 2);
  group.add(light);
  let lightOwner: Slot | null = null;

  // ---- per-slot meshes: bolt, flash, cloud, mist, and the two ground rings
  const slots: Slot[] = [];
  for (let i = 0; i < MAX_FX; i++) {
    const boltGeo = new THREE.BufferGeometry();
    const boltPos = new THREE.BufferAttribute(new Float32Array(PT_MAX * 4 * 3), 3);
    boltPos.setUsage(THREE.DynamicDrawUsage);
    boltGeo.setAttribute('position', boltPos);
    const idx: number[] = [];
    for (let p = 0; p < PT_MAX - 1; p++) {
      const a0 = p * 4, b0 = (p + 1) * 4;
      idx.push(a0, a0 + 1, b0 + 1, a0, b0 + 1, b0);
      idx.push(a0 + 2, a0 + 3, b0 + 3, a0 + 2, b0 + 3, b0 + 2);
    }
    boltGeo.setIndex(idx);
    const boltMat = new THREE.MeshBasicMaterial({
      transparent: true, opacity: 0, depthWrite: false, fog: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      color: new THREE.Color(0.92, 0.96, 1),
    });
    const bolt = new THREE.Mesh(boltGeo, boltMat);

    const flashMat = new THREE.MeshBasicMaterial({
      map: discTex, transparent: true, opacity: 0, depthWrite: false, fog: false,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      color: new THREE.Color(1, 1, 0.95),
    });
    const flash = new THREE.Mesh(flatGeo, flashMat);

    const cloudMat = new THREE.MeshBasicMaterial({
      map: stormTex, transparent: true, opacity: 0, depthWrite: false, fog: true,
      side: THREE.DoubleSide, color: new THREE.Color(0.3, 0.31, 0.36),
    });
    const cloud = new THREE.Mesh(flatGeo, cloudMat);

    const mistMat = new THREE.MeshBasicMaterial({
      map: discTex, transparent: true, opacity: 0, depthWrite: false, fog: true,
      side: THREE.DoubleSide, color: new THREE.Color(0.62, 0.68, 0.74),
    });
    const mist = new THREE.Mesh(flatGeo, mistMat);

    const bloomMat = new THREE.MeshBasicMaterial({
      map: ringTex, transparent: true, opacity: 0, depthWrite: false, fog: true,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      color: new THREE.Color(0.42, 0.95, 0.45),
    });
    const bloomRing = new THREE.Mesh(flatGeo, bloomMat);

    const witherMat = new THREE.MeshBasicMaterial({
      map: ringTex, transparent: true, opacity: 0, depthWrite: false, fog: true,
      side: THREE.DoubleSide, color: new THREE.Color(0.45, 0.38, 0.26),
    });
    const witherRing = new THREE.Mesh(flatGeo, witherMat);

    bolt.renderOrder = 3; flash.renderOrder = 3; cloud.renderOrder = 3;
    mist.renderOrder = 1; bloomRing.renderOrder = 1; witherRing.renderOrder = 1;
    for (const m of [bolt, flash, cloud, mist, bloomRing, witherRing]) {
      m.frustumCulled = false;
      m.visible = false;
      group.add(m);
    }
    slots.push({
      verb: null, radius: R, x: 0, y: 0, z: 0, age: 0, seed: 0,
      bolt, boltGeo, boltPos, boltMat, flash, flashMat,
      cloud, cloudMat, mist, mistMat,
      bloomRing, bloomMat, witherRing, witherMat,
    });
  }

  const deactivate = (s: Slot): void => {
    s.verb = null;
    s.bolt.visible = false; s.flash.visible = false;
    s.cloud.visible = false; s.mist.visible = false;
    s.bloomRing.visible = false; s.witherRing.visible = false;
    if (lightOwner === s) { lightOwner = null; light.intensity = 0; }
  };

  // per-frame scratch — the update path allocates nothing
  const _m = new THREE.Matrix4();
  const _q = new THREE.Quaternion();
  const _p = new THREE.Vector3();
  const _s = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 1, 0);

  return {
    group,

    spawn(verb, x, y, z, radius = R) {
      let s: Slot | null = null;
      for (const c of slots) if (c.verb === null) { s = c; break; }
      if (!s) {                       // pool full: the oldest spectacle yields
        s = slots[0];
        for (const c of slots) if (c.age > s.age) s = c;
        deactivate(s);
      }
      s.verb = verb;
      s.radius = Math.max(4, radius);
      s.x = x; s.y = y; s.z = z;
      s.age = 0;
      s.seed = hash3(Math.round(x), Math.round(z), 9241);
      const h = (i: number, salt: number) => hash01(s!.seed, i, salt);
      switch (verb) {
        case 'strike':
          buildBolt(s);
          s.bolt.visible = true;
          s.flash.visible = true;
          s.flash.position.set(x, y + 4, z);
          light.position.set(x, y + 45, z);
          lightOwner = s;
          break;
        case 'rain':
          s.cloud.visible = true;
          s.cloud.position.set(x, y + 330, z);
          s.cloud.rotation.y = h(4, 29) * TAU;
          s.mist.visible = true;
          s.mist.position.set(x, y + 6, z);
          s.mist.scale.set(2 * s.radius + 10, 1, 2 * s.radius + 10);
          break;
        case 'strengthen':
          s.bloomRing.visible = true;
          s.bloomRing.position.set(x, y + 3, z);
          break;
        case 'weaken':
          s.witherRing.visible = true;
          s.witherRing.position.set(x, y + 2.5, z);
          break;
        case 'omen':
          // OMEN HAD NO ANSWER AT ALL. `'omen'` was not in this union, not in
          // `DUR`, not in `SoundVerb`; `main.ts` returned before reaching
          // `spawn` and skipped `sound.cast` outright. So the one verb whose
          // entire point is that other minds respond gave the player a reticle
          // ring drawn as UI chrome and a line of text — while carrying the
          // deepest behavioural response in the build (a 6 km range, a
          // believed point offset into a doubt disc, trust moving ±0.25).
          //
          // It reuses `bloomRing` deliberately: **nothing new is drawn into
          // the world**, and the same geometry rising and fading cannot be
          // mistaken for weather. `update` gives it a different motion — it
          // rises and opens rather than washing outward — so it reads as a
          // sign and not as rain. Law 10 is unaffected either way: this is
          // render-only, hash-driven, and `sim.act('omen', …)` still dirties
          // not one chunk.
          s.bloomRing.visible = true;
          s.bloomRing.position.set(x, y + 3, z);
          break;
      }
    },

    update(tSec, dt) {
      for (const s of slots) {
        if (s.verb !== null) {
          s.age += dt;
          if (s.age >= DUR[s.verb]) deactivate(s);
        }
      }

      let rainCount = 0, moteCount = 0, dustCount = 0;
      for (const s of slots) {
        const a = s.age;
        switch (s.verb) {
          case 'strike': {
            // the crack: full stroke, a dim gap, the return stroke, decay
            const ph = hash01(s.seed, 3, 19) * TAU;
            let op = a < 0.05 ? 1 : a < 0.12 ? 0.38 : a < 0.19 ? 0.92
                   : Math.max(0, 0.55 * (1 - (a - 0.19) / 0.23));
            op = clamp(op * (0.9 + 0.1 * Math.sin(tSec * 87 + ph)), 0, 1);
            s.boltMat.opacity = op;
            const u = Math.min(1, a / 0.45);
            const fr = lerp(24, 130, 1 - (1 - u) * (1 - u));   // impact flash radius
            s.flash.scale.set(fr * 2, 1, fr * 2);
            s.flashMat.opacity = 0.9 * (1 - u) * (1 - u);
            if (lightOwner === s) light.intensity = 26 * op * (1 - 0.6 * u);
            break;
          }
          case 'rain': {
            // THE WORST OFFENDER AT CLOSE RANGE, measured: the cloud quad and
            // the fall height read NOTHING from the footprint, so a Rain
            // channelled into one plant hung a 900 x 666 m storm 265 m
            // overhead and dropped two hundred streaks through it into a
            // five-metre circle. At Presence altitude the sky filled up and
            // the plant was untouched by anything the eye could connect to it.
            const g = gauge(s.radius);
            const sky = clamp(g, 0.06, 1.3);
            const gather = smoothstep(0, 2, a);
            const leave = 1 - smoothstep(19.5, 22, a);
            s.cloudMat.opacity = 0.88 * gather * leave;
            const cs = lerp(0.5, 1, gather) * 900 * sky;
            s.cloud.scale.set(cs, 1, cs * 0.74);
            s.cloud.position.y = s.y + 330 * sky;
            const env = smoothstep(1.8, 3.6, a) * (1 - smoothstep(17.5, 19.8, a));
            s.mistMat.opacity = 0.13 * env;
            if (env > 0.001) {
              const rainN = countFor(RAIN_N, g, 14);
              for (let i = 0; i < rainN; i++) {
                const r = s.radius * 0.97 * Math.sqrt(hash01(s.seed, i, 31));
                const ang = hash01(s.seed, i, 37) * TAU;
                const speed = (85 + hash01(s.seed, i, 41) * 45) * clamp(sky, 0.25, 1);
                const len = (13 + hash01(s.seed, i, 43) * 9) * clamp(sky, 0.12, 1);
                const top = 265 * sky;                         // fall height, per gauge
                const span = top + len;                        // recycle top-to-ground
                const fall = (hash01(s.seed, i, 47) * span + a * speed) % span;
                _p.set(s.x + Math.cos(ang) * r, s.y + top - fall, s.z + Math.sin(ang) * r);
                _q.setFromAxisAngle(UP, hash01(s.seed, i, 53) * TAU);
                const w = 2.2 * env * clamp(sky, 0.14, 1);     // fades by thinning
                _s.set(w, len, w);
                _m.compose(_p, _q, _s);
                rain.setMatrixAt(rainCount++, _m);
              }
            }
            break;
          }
          case 'strengthen': {
            // THE SHIMMER WAVE, AND IT TRAVELS AT A SPEED.
            //
            // It used to reach the cast's whole footprint in a fixed 3 s
            // whatever that footprint was, which is a duration wearing a
            // rate's clothes: a five-metre cast's ring snapped to full size
            // inside one frame, and a watershed cast's crawled. A wave that
            // takes the same time to cross a tree and a valley is not a wave.
            // 140 m/s is the old behaviour at `R`, so a grove-sized cast is
            // untouched; the bounds keep a tiny cast visible for long enough
            // to be seen at all and a huge one from outlasting the effect.
            const g = gauge(s.radius);
            const sweep = clamp(s.radius / 140, 0.45, 6);
            const ru = Math.min(1, a / sweep);
            const rr = Math.max(0.01, s.radius * (1 - (1 - ru) * (1 - ru)));
            s.bloomRing.scale.set(rr * 2.5, 1, rr * 2.5);
            s.bloomMat.opacity = 0.5 * smoothstep(0, 0.2, a) * (1 - smoothstep(2.1, 3.4, a));
            const moteN = countFor(MOTE_N, g);
            const moteSz = clamp(g, 0.10, 1.5);      // metres of mote, per gauge
            const rise = clamp(g, 0.12, 1.4);
            for (let i = 0; i < moteN; i++) {
              const birth = hash01(s.seed, i, 61) * 3.4;
              const life = 3.2 + hash01(s.seed, i, 67) * 1.7;
              const t = a - birth;
              if (t <= 0 || t >= life) continue;
              const r = s.radius * 0.06 + (s.radius * 0.89) * Math.sqrt(hash01(s.seed, i, 71));
              const ang = hash01(s.seed, i, 73) * TAU;
              const swPh = hash01(s.seed, i, 83) * TAU;
              _p.set(s.x + Math.cos(ang) * r + Math.sin(tSec * 0.9 + swPh) * 3 * rise,
                     s.y + 2 * rise + t * (4 + hash01(s.seed, i, 79) * 5) * rise,
                     s.z + Math.sin(ang) * r + Math.cos(tSec * 0.7 + swPh) * 3 * rise);
              _q.setFromAxisAngle(UP, hash01(s.seed, i, 87) * TAU);
              const e = Math.min(1, t / 0.5) * (1 - smoothstep(0.65, 1, t / life));
              const sz = (2.6 + hash01(s.seed, i, 89) * 3.2) * e * moteSz;
              _s.set(sz, 1, sz);
              _m.compose(_p, _q, _s);
              motes.setMatrixAt(moteCount++, _m);
            }
            break;
          }
          case 'omen': {
            // A SIGN, NOT A FALL. Everything here is chosen against being
            // mistaken for weather or for a blessing:
            //
            //  · it RISES. Every other verb's spectacle comes down or washes
            //    out across the ground; this one lifts off it, because nothing
            //    landed.
            //  · it holds a narrow ring rather than a spreading wave — the
            //    strengthen ring reaches the cast's full footprint in 3 s, and
            //    a player who has seen both must never read them as the same
            //    gesture at two strengths.
            //  · it does not resolve. The opacity is still falling when the
            //    effect ends, so the moment closes without an answer, which is
            //    exactly what an omen is: the world was asked a question and
            //    the asking is all the god gets to do.
            //
            // Motes only — no dust, no mist, no light. It touches nothing.
            const g = gauge(s.radius);
            const lift = clamp(g, 0.14, 1.4);
            const ru = smoothstep(0, 1.6, a);
            const rr = Math.max(0.01, s.radius * (0.22 + 0.30 * ru));
            s.bloomRing.scale.set(rr * 2.5, 1, rr * 2.5);
            s.bloomRing.position.y = s.y + (3 + a * 5.5) * lift;
            s.bloomMat.opacity = 0.42 * smoothstep(0, 0.35, a) * (1 - smoothstep(1.4, 4.3, a));
            const omenN = countFor(MOTE_N, g);
            const omenSz = clamp(g, 0.10, 1.5);
            for (let i = 0; i < omenN; i++) {
              const birth = hash01(s.seed, i, 149) * 1.5;
              const life = 2.4 + hash01(s.seed, i, 151) * 1.4;
              const t = a - birth;
              if (t <= 0 || t >= life) continue;
              // a narrow column, drifting up and inward — the opposite of the
              // strengthen motes, which scatter outward across the footprint
              const r0 = s.radius * (0.10 + 0.34 * Math.sqrt(hash01(s.seed, i, 157)));
              const ang = hash01(s.seed, i, 163) * TAU;
              const inward = 1 - 0.45 * (t / life);
              _p.set(s.x + Math.cos(ang) * r0 * inward,
                     s.y + (3 + t * (11 + hash01(s.seed, i, 167) * 7)) * lift,
                     s.z + Math.sin(ang) * r0 * inward);
              _q.setFromAxisAngle(UP, hash01(s.seed, i, 173) * TAU);
              const e = Math.min(1, t / 0.35) * (1 - smoothstep(0.4, 1, t / life));
              const sz = (1.9 + hash01(s.seed, i, 179) * 2.1) * e * omenSz;
              _s.set(sz, 1, sz);
              _m.compose(_p, _q, _s);
              motes.setMatrixAt(moteCount++, _m);
            }
            break;
          }
          case 'weaken': {
            // the inverse wave: a dun ring closing in, dust on the wind
            const g = gauge(s.radius);
            // dust is the widest particle in the file — 16–30 m across at
            // gauge 1 — so it is the one that most obviously belonged to a
            // valley when it was thrown over a sapling
            const dustSz = clamp(g, 0.08, 1.4);
            const gust = clamp(g, 0.12, 1.3);
            const rr = Math.max(0.01, s.radius * (1 - smoothstep(0.2, 6, a)));
            s.witherRing.scale.set(rr * 2.5, 1, rr * 2.5);
            s.witherMat.opacity = 0.34 * smoothstep(0, 0.7, a) * (1 - smoothstep(5.2, 6.6, a));
            const dustN = countFor(DUST_N, g, 6);
            for (let i = 0; i < dustN; i++) {
              const birth = hash01(s.seed, i, 101) * 4.4;
              const life = 2.2 + hash01(s.seed, i, 103) * 1.6;
              const t = a - birth;
              if (t <= 0 || t >= life) continue;
              const r = Math.sqrt(hash01(s.seed, i, 107)) * (s.radius * 0.9);
              const ang = hash01(s.seed, i, 109) * TAU;
              const windv = (15 + hash01(s.seed, i, 113) * 12) * gust;  // gusting along +x
              _p.set(s.x + Math.cos(ang) * r + t * windv,
                     s.y + (1.5 + hash01(s.seed, i, 127) * 3) * gust,
                     s.z + Math.sin(ang) * r);
              _q.identity();
              const e = Math.min(1, t / 0.4) * (1 - smoothstep(0.55, 1, t / life));
              _s.set((16 + hash01(s.seed, i, 131) * 14) * e * dustSz, 1,
                     (3 + hash01(s.seed, i, 137) * 2) * e * dustSz);
              _m.compose(_p, _q, _s);
              dust.setMatrixAt(dustCount++, _m);
            }
            break;
          }
        }
      }

      rain.count = rainCount;
      motes.count = moteCount;
      dust.count = dustCount;
      rain.instanceMatrix.needsUpdate = true;
      motes.instanceMatrix.needsUpdate = true;
      dust.instanceMatrix.needsUpdate = true;
    },
  };
}
